/**
 * Wave TC — platform admin edits a firm's contract: reactivate a lapsed trial,
 * extend a term, or convert to a paid plan. Lifts read-only on save.
 * Declared at module scope (standing React rule).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { getAuthToken } from '../../lib/authToken';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';

export interface ContractTenant {
  id: string;
  name: string;
  status: string;
  type: string;
  plan: string;
  readOnlyMode?: boolean;
  trialLifecycleState?: string | null;
  subscription?: any;
  metadata?: any;
}

interface Props {
  tenant: ContractTenant | null;
  onClose: () => void;
  onSaved: () => void;
}

const isoDay = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const addDays = (days: number) => isoDay(new Date(Date.now() + days * 86400000));
const addYears = (years: number) => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + years);
  return isoDay(d);
};
const showDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString() : '—');

const TenantContractDialog: React.FC<Props> = ({ tenant, onClose, onSaved }) => {
  const [type, setType] = useState('trial');
  const [plan, setPlan] = useState('basic');
  const [termEnd, setTermEnd] = useState('');
  const [billingCycle, setBillingCycle] = useState('');
  const [amount, setAmount] = useState('');
  const [seatCap, setSeatCap] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!tenant) return;
    setType(tenant.type || 'trial');
    setPlan(tenant.plan || 'basic');
    setTermEnd(addDays(30));
    setBillingCycle(tenant.subscription?.billingCycle || '');
    setAmount(tenant.subscription?.amount != null ? String(tenant.subscription.amount) : '');
    setSeatCap(tenant.metadata?.seatCap != null ? String(tenant.metadata.seatCap) : '');
    setNote('');
  }, [tenant]);

  const isTrial = type === 'trial' || type === 'demo';
  const locked = Boolean(tenant?.readOnlyMode) || tenant?.status === 'suspended' || tenant?.status === 'inactive';
  const history: any[] = useMemo(
    () => (Array.isArray(tenant?.metadata?.contractHistory) ? [...tenant!.metadata.contractHistory].reverse() : []),
    [tenant],
  );
  const termError = isTrial && !termEnd
    ? 'A trial or demo needs an end date'
    : termEnd && termEnd <= isoDay(new Date())
      ? 'Choose a date after today'
      : '';

  const save = async () => {
    if (!tenant) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/admin/super/tenants/${tenant.id}/contract`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          plan,
          termEnd: termEnd || null,
          billingCycle: !isTrial && billingCycle ? billingCycle : null,
          amount: !isTrial && amount !== '' ? Number(amount) : null,
          seatCap: seatCap !== '' ? Number(seatCap) : null,
          note: note.trim(),
        }),
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Unable to update contract'));
      const json = await res.json();
      toast.success(json.message || 'Contract updated');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(toUserErrorMessage(err, 'Unable to update contract'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(tenant)} onClose={() => !saving && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>Edit contract — {tenant?.name}</DialogTitle>
      <DialogContent dividers>
        {tenant && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Chip size="small" label={`Status: ${tenant.status}`} />
            <Chip size="small" label={`${tenant.type} / ${tenant.plan}`} />
            {tenant.readOnlyMode && <Chip size="small" color="error" label="Read-only" />}
            {tenant.trialLifecycleState === 'grace' && <Chip size="small" color="warning" label="Grace period" />}
            {tenant.subscription?.trialEndDate && <Chip size="small" variant="outlined" label={`Trial ends ${showDate(tenant.subscription.trialEndDate)}`} />}
            {tenant.subscription?.contractEnd && <Chip size="small" variant="outlined" label={`Contract ends ${showDate(tenant.subscription.contractEnd)}`} />}
          </Stack>
        )}
        {locked && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Saving reactivates this firm: read-only is lifted immediately and its users regain full access. No firm data is changed.
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="tc-type">Tenant type</InputLabel>
              <Select labelId="tc-type" label="Tenant type" value={type} onChange={(e) => setType(e.target.value)}>
                <MenuItem value="trial">Trial</MenuItem>
                <MenuItem value="demo">Demo / pilot</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="enterprise">Enterprise</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="tc-plan">Plan</InputLabel>
              <Select labelId="tc-plan" label="Plan" value={plan} onChange={(e) => setPlan(e.target.value)}>
                <MenuItem value="basic">Basic</MenuItem>
                <MenuItem value="professional">Professional</MenuItem>
                <MenuItem value="enterprise">Enterprise</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label={isTrial ? 'Trial ends' : 'Contract ends (optional)'}
              InputLabelProps={{ shrink: true }}
              value={termEnd}
              onChange={(e) => setTermEnd(e.target.value)}
              error={Boolean(termError)}
              helperText={termError || (isTrial ? 'Required — the nightly check locks the firm after this date plus the grace period.' : 'Leave blank for an open-ended contract.')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 1 }}>
              {isTrial ? (
                [14, 30, 60, 90].map((d) => (
                  <Button key={d} size="small" variant="outlined" onClick={() => setTermEnd(addDays(d))}>+{d} days</Button>
                ))
              ) : (
                <>
                  <Button size="small" variant="outlined" onClick={() => setTermEnd(addDays(30))}>+1 month</Button>
                  <Button size="small" variant="outlined" onClick={() => setTermEnd(addYears(1))}>+1 year</Button>
                  <Button size="small" variant="outlined" onClick={() => setTermEnd('')}>Open-ended</Button>
                </>
              )}
            </Stack>
          </Grid>
          {!isTrial && (
            <>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="tc-cycle">Billing cycle</InputLabel>
                  <Select labelId="tc-cycle" label="Billing cycle" value={billingCycle} onChange={(e) => setBillingCycle(e.target.value)}>
                    <MenuItem value="">Not set</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="quarterly">Quarterly</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth type="number" label="Amount per cycle (USD)" value={amount}
                  onChange={(e) => setAmount(e.target.value)} inputProps={{ min: 0, step: '0.01' }} />
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="number" label="Seat cap (optional)" value={seatCap}
              onChange={(e) => setSeatCap(e.target.value)} inputProps={{ min: 1, max: 1000 }} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              multiline
              minRows={2}
              label="Reason (kept in the audit trail)"
              placeholder="e.g. Firm signed annual Professional agreement on 9/23; invoiced offline"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              inputProps={{ maxLength: 500 }}
            />
          </Grid>
        </Grid>
        {!isTrial && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Online card billing isn&apos;t configured, so this is recorded as an <strong>offline</strong> contract — the platform will not
            charge the firm automatically. Invoice them outside the system.
          </Alert>
        )}
        {history.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Contract history</Typography>
            {history.slice(0, 10).map((h, i) => (
              <Typography key={i} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {showDate(h.at)} · {h.byEmail || 'platform admin'}: {h.from?.type}/{h.from?.plan}
                {h.from?.readOnlyMode ? ' (read-only)' : ''} → {h.to?.type}/{h.to?.plan}
                {h.to?.trialEndDate || h.to?.contractEnd ? ` until ${showDate(h.to.trialEndDate || h.to.contractEnd)}` : ''} — {h.note}
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || Boolean(termError) || note.trim().length < 3}>
          {saving ? 'Saving…' : locked ? 'Save & reactivate' : 'Save contract'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TenantContractDialog;
